import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import FilterSidebar from './FilterSidebar';
import { useTranslation } from 'react-i18next';

interface MobileFilterSheetProps {
    isOpen: boolean;
    onClose: () => void;
}

const MobileFilterSheet: React.FC<MobileFilterSheetProps> = ({ isOpen, onClose }) => {
    const { t } = useTranslation('marketplace');

    // Prevent body scroll when sheet is open
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [isOpen]);

    return (
        <AnimatePresence>
            {isOpen && (
                <>
                    {/* Backdrop */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        onClick={onClose}
                        className="fixed inset-0 bg-black/60 backdrop-blur-md z-[100]"
                    />

                    {/* Sheet */}
                    <motion.div
                        initial={{ y: '100%' }}
                        animate={{ y: 0 }}
                        exit={{ y: '100%' }}
                        transition={{ type: 'spring', damping: 30, stiffness: 300 }}
                        className="fixed bottom-0 left-0 right-0 z-[101] bg-white dark:bg-gray-950 rounded-t-[2.5rem] shadow-2xl max-h-[90vh] flex flex-col border-t border-gray-100 dark:border-gray-800"
                    >
                        {/* Handle */}
                        <div className="flex justify-center pt-3 pb-2">
                            <div className="w-12 h-1.5 bg-gray-100 dark:bg-gray-800 rounded-full" />
                        </div>

                        {/* Header */}
                        <div className="flex items-center justify-between px-6 pb-4 border-b border-gray-100 dark:border-gray-800">
                            <h2 className="text-lg font-black text-gray-900 dark:text-white">
                                {t('mobile.filter')}
                            </h2>
                            <button
                                onClick={onClose}
                                className="p-2 rounded-full bg-gray-50 dark:bg-gray-800 text-gray-400 dark:text-gray-500 hover:text-gray-900 dark:hover:text-white transition-colors"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        {/* Scrollable Filter Content */}
                        <div className="flex-1 overflow-y-auto px-6 py-4">
                            <FilterSidebar className="w-full" />
                        </div>

                        {/* Sticky Footer - Added extra bottom padding for BottomNav safety */}
                        <div className="sticky bottom-0 bg-white dark:bg-gray-950 border-t border-gray-100 dark:border-gray-800 px-6 pt-4 pb-8 flex gap-3">
                            <motion.button
                                whileTap={{ scale: 0.98 }}
                                onClick={onClose}
                                className="flex-1 py-4 bg-gray-950 dark:bg-white text-white dark:text-gray-950 rounded-2xl text-[11px] font-black uppercase tracking-[0.2em] shadow-premium active:scale-95 transition-all"
                            >
                                {t('mobile.showResults')}
                            </motion.button>
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
};

export default MobileFilterSheet;
